import { differenceInDays } from "date-fns";

import type { ClinicClock } from "@/lib/clinic-clock";
import {
  formatCurrency,
  formatDate,
  formatPhoneNumber,
  formatToHumanReadable,
} from "@/lib/format-data";

import {
  BOOLEAN_FILTER_OPTIONS,
  singleBooleanValue,
} from "./filter-types/boolean";
import { FilterOption, FilterTypes } from "./filter-types/types";

const MONTH_NAMES_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

/**
 * Format a `--MM-DD` (or bare `MM-DD`) string as a short human label like
 * "Mar 15". Used by the birthday filter pill display.
 */
const formatBirthdayLabel = (val: string): string => {
  const stripped = val.startsWith("--") ? val.slice(2) : val;
  const [m, d] = stripped.split("-");
  const month = Number.parseInt(m, 10);
  const day = Number.parseInt(d, 10);
  const monthName = MONTH_NAMES_SHORT[month - 1];
  if (!monthName || !Number.isFinite(day)) return "";
  return `${monthName} ${day}`;
};

const formatArrayValues = (value: string[]) => {
  if (value.length === 1) {
    return value[0];
  } else if (value.length === 2) {
    return `${value[0]} and ${value[1]}`;
  } else {
    return `${value[0]}, ${value[1]} and ${value.length - 2} more`;
  }
};

/**
 * Unified function to format filter display values for all filter types.
 * This centralizes all formatting logic so individual filter components
 * don't need to worry about display value formatting.
 */
export function formatFilterDisplayValue(
  filterValue: any,
  type: FilterTypes,
  options: FilterOption[] | undefined,
  /** The caller's `useClinicClock()`, for "Today" and "Last N days". */
  clock: ClinicClock,
): { fullDisplayValue: string; truncatedDisplayValue: string } {
  if (
    filterValue === undefined ||
    filterValue === null ||
    filterValue === "" ||
    (Array.isArray(filterValue) && filterValue.length === 0)
  ) {
    return { fullDisplayValue: "", truncatedDisplayValue: "" };
  }

  switch (type) {
    case "toggle":
      return {
        fullDisplayValue: filterValue === true ? "On" : "",
        truncatedDisplayValue: filterValue === true ? "On" : "",
      };

    case "boolean": {
      const choice = singleBooleanValue(filterValue);
      if (choice === undefined || choice === null || choice === "") {
        return { fullDisplayValue: "", truncatedDisplayValue: "" };
      }
      const option = (options?.length ? options : BOOLEAN_FILTER_OPTIONS).find(
        (opt) => String(opt.value) === String(choice),
      );
      const label = option?.label ?? formatToHumanReadable(String(choice));
      return { fullDisplayValue: label, truncatedDisplayValue: label };
    }

    case "select": {
      // Single select - look up label from options
      if (typeof filterValue === "string" && filterValue) {
        // Handle custom "other: " prefixed values
        if (filterValue.startsWith("other: ")) {
          const otherValue = filterValue.replace(/^other:\s*/i, "");
          return {
            fullDisplayValue: `Other: ${otherValue}`,
            truncatedDisplayValue:
              otherValue.length > 10
                ? `Other: ${otherValue.substring(0, 10)}...`
                : `Other: ${otherValue}`,
          };
        }
        const option = options?.find((opt) => opt.value === filterValue);
        const label = option?.label || formatToHumanReadable(filterValue);
        return {
          fullDisplayValue: label,
          truncatedDisplayValue:
            label.length > 15 ? label.substring(0, 15) + "..." : label,
        };
      }
      return { fullDisplayValue: "", truncatedDisplayValue: "" };
    }

    case "multiselect":
    case "location": {
      // Normalize to array (URL params may pass single string value)
      const values = Array.isArray(filterValue)
        ? filterValue
        : [filterValue].filter(Boolean);
      if (values.length > 0) {
        const fullLabels = values.map((value) => {
          // Handle custom "other: " prefixed values
          if (typeof value === "string" && value.startsWith("other: ")) {
            return `Other: ${value.replace(/^other:\s*/i, "")}`;
          }
          const option = options?.find((opt) => opt.value === value);
          return option ? option.label : formatToHumanReadable(value);
        });
        const truncatedLabels = values.map((value) => {
          // Handle custom "other: " prefixed values
          if (typeof value === "string" && value.startsWith("other: ")) {
            const otherValue = value.replace(/^other:\s*/i, "");
            return otherValue.length > 10
              ? `Other: ${otherValue.substring(0, 10)}...`
              : `Other: ${otherValue}`;
          }
          const option = options?.find((opt) => opt.value === value);
          const label = option?.label;
          if (label) {
            return label.length > 10 ? label.substring(0, 10) + "..." : label;
          }
          return formatToHumanReadable(value);
        });
        return {
          fullDisplayValue: formatArrayValues(fullLabels),
          truncatedDisplayValue: formatArrayValues(truncatedLabels),
        };
      }
      return { fullDisplayValue: "", truncatedDisplayValue: "" };
    }

    case "sort": {
      const option = options?.find(
        (opt) =>
          typeof filterValue === "object" &&
          "sortBy" in filterValue &&
          opt.value === filterValue.sortBy,
      );
      if (option) {
        const direction = filterValue.sortDirection === "asc" ? "↑" : "↓";
        const fullLabel = `${option.label} ${direction}`;
        const truncatedLabel =
          option.label.length > 10
            ? `${option.label.substring(0, 10)}... ${direction}`
            : fullLabel;
        return {
          fullDisplayValue: fullLabel,
          truncatedDisplayValue: truncatedLabel,
        };
      }
      // Fallback if option not found
      if (
        typeof filterValue === "object" &&
        "sortBy" in filterValue &&
        "sortDirection" in filterValue
      ) {
        const direction = filterValue.sortDirection === "asc" ? "↑" : "↓";
        const sortBy = String(filterValue.sortBy);
        const fullLabel = `${sortBy} ${direction}`;
        const truncatedLabel =
          sortBy.length > 10
            ? `${sortBy.substring(0, 10)}... ${direction}`
            : fullLabel;
        return {
          fullDisplayValue: fullLabel,
          truncatedDisplayValue: truncatedLabel,
        };
      }
      return { fullDisplayValue: "", truncatedDisplayValue: "" };
    }

    case "text":
      return {
        fullDisplayValue: formatToHumanReadable(filterValue),
        truncatedDisplayValue: formatToHumanReadable(filterValue),
      };

    case "id":
      // Several ids (a link to a combined notification's rows) read as a count.
      if (Array.isArray(filterValue) && filterValue.length > 1) {
        const count = `${filterValue.length} IDs`;
        return { fullDisplayValue: count, truncatedDisplayValue: count };
      }
      // IDs should not be formatted - display as-is
      return {
        fullDisplayValue: String(filterValue),
        truncatedDisplayValue: String(filterValue),
      };

    case "number":
      // Handle number comparison format { value: number, comparison: 'eq' | 'gte' | 'lte' }
      if (
        typeof filterValue === "object" &&
        filterValue !== null &&
        "value" in filterValue &&
        "comparison" in filterValue
      ) {
        const { value, comparison } = filterValue;
        const comparisonSymbols: Record<
          string,
          { symbol: string; label: string }
        > = {
          eq: { symbol: "=", label: "equals" },
          gte: { symbol: "≥", label: "greater than or equal to" },
          lte: { symbol: "≤", label: "less than or equal to" },
        };
        const comp = comparisonSymbols[comparison] || comparisonSymbols.eq;
        return {
          fullDisplayValue: `${comp.label} ${value}`,
          truncatedDisplayValue: `${comp.symbol} ${value}`,
        };
      }
      // Fallback for simple number values
      return {
        fullDisplayValue: formatToHumanReadable(String(filterValue)),
        truncatedDisplayValue: formatToHumanReadable(String(filterValue)),
      };

    case "phoneNumber":
      return {
        fullDisplayValue: formatPhoneNumber(filterValue),
        truncatedDisplayValue: formatPhoneNumber(filterValue),
      };

    case "birthday":
      if (Array.isArray(filterValue) && filterValue.length === 2) {
        const [start, end] = filterValue;
        if (typeof start !== "string" || typeof end !== "string") {
          return { fullDisplayValue: "", truncatedDisplayValue: "" };
        }
        const startDisplay = formatBirthdayLabel(start);
        const endDisplay = formatBirthdayLabel(end);
        if (start === end) {
          return {
            fullDisplayValue: startDisplay,
            truncatedDisplayValue: startDisplay,
          };
        }
        return {
          fullDisplayValue: `${startDisplay} - ${endDisplay}`,
          truncatedDisplayValue: `${startDisplay} - ${endDisplay}`,
        };
      }
      return { fullDisplayValue: "", truncatedDisplayValue: "" };

    case "date":
      if (Array.isArray(filterValue) && filterValue.length === 2) {
        const [start, end] = filterValue;

        if (typeof start === "string" && typeof end !== "string") {
          return {
            fullDisplayValue: `Starting ${formatDate(start)}`,
            truncatedDisplayValue: `≥ ${formatDate(start)}`,
          };
        }
        if (typeof start !== "string" && typeof end === "string") {
          return {
            fullDisplayValue: `Through ${formatDate(end)}`,
            truncatedDisplayValue: `≤ ${formatDate(end)}`,
          };
        }
        if (typeof start !== "string" || typeof end !== "string") {
          return { fullDisplayValue: "", truncatedDisplayValue: "" };
        }

        const startDate = new Date(start + "T00:00:00");

        // Handle special date filter types
        if (end === "9000-12-31") {
          // "on or after" filter
          return {
            fullDisplayValue: `Starting ${formatDate(start)}`,
            truncatedDisplayValue: `≥ ${formatDate(start)}`,
          };
        } else if (start === "1970-01-01") {
          // "before" filter
          return {
            fullDisplayValue: `Before ${formatDate(end)}`,
            truncatedDisplayValue: `≤ ${formatDate(end)}`,
          };
        } else if (start === end) {
          // Exact date filter - check if it's today
          if (start === clock.today()) {
            return {
              fullDisplayValue: "Today",
              truncatedDisplayValue: "Today",
            };
          }
          return {
            fullDisplayValue: `On ${formatDate(start)}`,
            truncatedDisplayValue: formatDate(start),
          };
        } else {
          // Date range filter
          // Check if it's "in the last X days" by comparing with today
          if (end === clock.today()) {
            // Use date-fns differenceInDays for accurate calculation
            // Normalize endDate to midnight for accurate day calculation
            const endDateNormalized = new Date(end + "T00:00:00");
            // differenceInDays returns the number of full days between dates
            // Since we want inclusive days (including both start and end), we add 1
            const daysDiff = differenceInDays(endDateNormalized, startDate) + 1;
            if (daysDiff > 0 && daysDiff <= 99) {
              const displayValue =
                daysDiff === 1 ? "Today" : `Last ${daysDiff} days`;
              return {
                fullDisplayValue: displayValue,
                truncatedDisplayValue: displayValue,
              };
            }
          }

          return {
            fullDisplayValue: `${formatDate(start)} to ${formatDate(end)}`,
            truncatedDisplayValue: `${formatDate(start)} - ${formatDate(end)}`,
          };
        }
      }
      return { fullDisplayValue: "", truncatedDisplayValue: "" };

    case "date-select":
      if (filterValue && Array.isArray(filterValue) && filterValue[0]) {
        const dateValue = filterValue[0];
        // Handle special --MM-DD format for month/day only filtering
        if (dateValue.startsWith("--")) {
          const parts = dateValue.slice(2).split("-");
          const month = parts[0];
          const day = parts[1];
          if (month && day) {
            const monthNames = [
              "January",
              "February",
              "March",
              "April",
              "May",
              "June",
              "July",
              "August",
              "September",
              "October",
              "November",
              "December",
            ];
            const monthIndex = parseInt(month) - 1;
            const monthName = monthNames[monthIndex];
            if (monthName) {
              return {
                fullDisplayValue: `${monthName} ${parseInt(day)}`,
                truncatedDisplayValue: `${monthName} ${parseInt(day)}`,
              };
            }
          }
        } else {
          return {
            fullDisplayValue: formatDate(dateValue),
            truncatedDisplayValue: formatDate(dateValue),
          };
        }
      }
      return { fullDisplayValue: "", truncatedDisplayValue: "" };

    case "provider": {
      // Provider filter stores provider IDs (array) - look up names from options
      const providerValues = Array.isArray(filterValue)
        ? filterValue
        : [filterValue].filter(Boolean);
      if (providerValues.length > 0) {
        const fullLabels = providerValues.map((v) => {
          const opt = options?.find((o) => o.value === v);
          return opt?.label ?? formatToHumanReadable(v);
        });
        const truncatedLabels = fullLabels.map((label) =>
          label.length > 10 ? `${label.substring(0, 10)}...` : label,
        );
        return {
          fullDisplayValue: formatArrayValues(fullLabels),
          truncatedDisplayValue: formatArrayValues(truncatedLabels),
        };
      }
      return { fullDisplayValue: "", truncatedDisplayValue: "" };
    }

    case "number-range":
      // Number range filter stores values as [lower | null, upper | null]
      if (Array.isArray(filterValue) && filterValue.length === 2) {
        const [lower, upper] = filterValue;
        if (lower != null && upper != null && lower === upper) {
          return {
            fullDisplayValue: `exactly ${lower}`,
            truncatedDisplayValue: `exactly ${lower}`,
          };
        }
        if (lower != null && upper == null) {
          return {
            fullDisplayValue: `greater than or equal to ${lower}`,
            truncatedDisplayValue: `≥ ${lower}`,
          };
        }
        if (lower == null && upper != null) {
          return {
            fullDisplayValue: `less than or equal to ${upper}`,
            truncatedDisplayValue: `≤ ${upper}`,
          };
        }
        if (lower != null && upper != null) {
          return {
            fullDisplayValue: `between ${lower} and ${upper}`,
            truncatedDisplayValue: `${lower} - ${upper}`,
          };
        }
      }
      return { fullDisplayValue: "", truncatedDisplayValue: "" };

    case "dollar":
      // Dollar filter stores values as [lowerBound, upperBound] in cents.
      // null means unbounded; legacy data may use 0 / POSTGRES_MAX_INT instead.
      if (Array.isArray(filterValue) && filterValue.length === 2) {
        const [lower, upper] = filterValue as [number | null, number | null];
        const isUpperUnbounded = upper == null || upper >= 2147483647;
        const isLowerUnbounded = lower == null || lower === 0;

        // Check for exact match
        if (
          lower != null &&
          upper != null &&
          lower === upper &&
          !isUpperUnbounded
        ) {
          return {
            fullDisplayValue: `exactly ${formatCurrency(lower)}`,
            truncatedDisplayValue: `exactly ${formatCurrency(lower)}`,
          };
        }
        // Check for "greater than or equal to"
        if (isUpperUnbounded && !isLowerUnbounded) {
          return {
            fullDisplayValue: `greater than or equal to ${formatCurrency(lower!)}`,
            truncatedDisplayValue: `≥ ${formatCurrency(lower!)}`,
          };
        }
        // Check for "less than or equal to"
        if (isLowerUnbounded && !isUpperUnbounded) {
          return {
            fullDisplayValue: `less than or equal to ${formatCurrency(upper!)}`,
            truncatedDisplayValue: `≤ ${formatCurrency(upper!)}`,
          };
        }
        // Range filter
        if (lower != null && upper != null) {
          return {
            fullDisplayValue: `between ${formatCurrency(lower)} and ${formatCurrency(upper)}`,
            truncatedDisplayValue: `${formatCurrency(lower)} - ${formatCurrency(upper)}`,
          };
        }
      }
      return {
        fullDisplayValue: filterValue ? "Set" : "",
        truncatedDisplayValue: filterValue ? "Set" : "",
      };

    default:
      return {
        fullDisplayValue: String(filterValue),
        truncatedDisplayValue: String(filterValue),
      };
  }
}

export { formatArrayValues };
